'use client';

import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import TadmLogo from '@/components/tadm-logo';
import { useEffect } from 'react';
import { getCurrentUserRole, roleToRedirectPath } from '@/lib/auth';
import { useToast } from '@/hooks/use-toast';
import { signInAndGetRole } from '@/lib/auth';

const formSchema = z.object({
  counterNumber: z.string().min(1, 'Room number is required'),
  pin: z.string().min(1, 'PIN is required'),
});

export default function VisitorCounterPage() {
  const router = useRouter();
  const { toast } = useToast();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      counterNumber: '',
      pin: '',
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const current = await getCurrentUserRole();
        if (current) {
          const url = roleToRedirectPath(current.role, current.email);
          router.replace(url);
        }
      } catch (_) {}
    })();
  }, [router]);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      const rawCounter = values.counterNumber.toLowerCase();
      const emailAddr = rawCounter.includes('@') ? rawCounter : `${rawCounter}@tadm.com`;
      const { role } = await signInAndGetRole(emailAddr, values.pin);
      if (role !== 'visitor') {
        toast({ title: 'Access denied', description: 'Your role does not have access to the Visitor portal.', variant: 'destructive' });
        return;
      }
      router.push('/visitor');
    } catch (e: any) {
      const message = e?.message || 'Login failed. Please try again.';
      toast({ title: 'Login error', description: message, variant: 'destructive' });
    }
  }

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4 dark:bg-gray-900">
      <div className="w-full max-w-md">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 h-16 w-16">
              <TadmLogo />
            </div>
            <CardTitle className="font-headline text-3xl">Visitor Room Setup</CardTitle>
            <CardDescription>Please enter room number and PIN to continue.</CardDescription>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="counterNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Room Number</FormLabel>
                      <FormControl>
                        <Input type="text" placeholder="e.g., SB1" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>PIN</FormLabel>
                      <FormControl>
                        <Input type="password" placeholder="Enter your PIN" autoComplete="current-password" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                 <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting ? 'Continuing...' : 'Continue'}
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
      </div>
    </main>
  );
}
